import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ChevronDown, ChevronUp, Save, ArrowLeft, Trash2, AlertCircle, Edit, FileText, Send } from 'lucide-react';
import { ProjectAndCustomer } from '../project/projectType';
import { Estimate, EstimateProjectCustomer, default_terms_and_conditions, EstimateWithItemsRequest } from '../estimate/estimateType';
import { EstimateData as AIEstimateData } from './smartEstimateAssistantType';
import { formatAddress, formatCurrency } from '../lib/global_function';
import EstimateApi from '../estimate/estimateApi';

interface EditableLineItem {
  tempId: string;
  itemized_estimate_id: string;
  code: string;
  description: string;
  unit_price_dollar: number;
  quantity: number;
  sub_total_dollar: number;
  cost_type: 'all' | 'labor' | 'material';
  duration_hour: number;
  estimate_id: string;
  is_optional: boolean;
}

const SmartGeneratedEstimatePage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Estimate', path: '/main/project/estimate' },
    { label: 'Smart Assistant', path: '/main/project/estimate/smartassistant' },
    { label: 'Generated Estimate', path: '/main/project/estimate/smartgeneratedestimate' },
  ];

  const navigate = useNavigate();
  const location = useLocation();

  const [estimateData, setEstimateData] = useState<AIEstimateData | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<ProjectAndCustomer | null>(null);
  const [conversationId, setConversationId] = useState<string>('');
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [savedEstimateId, setSavedEstimateId] = useState<string | null>(null);

  // Editable data
  const [estimateName, setEstimateName] = useState<string>('');
  const [estimateDescription, setEstimateDescription] = useState<string>('');
  const [lineItems, setLineItems] = useState<EditableLineItem[]>([]);
  const [taxRate, setTaxRate] = useState<number>(0);
  const [discount, setDiscount] = useState<number>(0);
  const [termsAndConditions, setTermsAndConditions] = useState<string>(default_terms_and_conditions);
  const [timeToCompleteDays, setTimeToCompleteDays] = useState<number>(0);
  const [timeToCompleteHours, setTimeToCompleteHours] = useState<number>(0);

  // Calculated totals
  const [totals, setTotals] = useState({
    subtotal: 0,
    taxAmount: 0,
    finalTotal: 0,
    totalHours: 0,
    totalLaborHours: 0,
    optionalDollar: 0,
    requiredDollar: 0,
  });

  useEffect(() => {
    if (location.state) {
      const { estimateData: data, selectedCustomer: customer, conversationId: convId } = location.state;

      if (!data || !customer) {
        setErrorMessage('Missing estimate data. Please return to the Smart Assistant.');
        return;
      }

      setEstimateData(data);
      setSelectedCustomer(customer);
      setConversationId(convId || '');

      // Initialize editable fields
      setEstimateName(data.estimate.name || 'AI Generated Estimate');
      setEstimateDescription(data.estimate.description || '');

      // Initialize tax rate and discount if provided
      if (data.estimate.tax_rate !== null && data.estimate.tax_rate !== undefined) {
        setTaxRate(Number(data.estimate.tax_rate) || 0);
      }
      if (data.estimate.discount !== null && data.estimate.discount !== undefined) {
        setDiscount(Number(data.estimate.discount) || 0);
      }

      // Initialize line items with editable structure
      const initialItems: EditableLineItem[] = data.itemized_estimates.map((item: any, index: number) => {
        const quantity = parseFloat(item.quantity?.toString() || '0') || 0;
        const subtotal = Number(item.sub_total_dollar) || 0;
        let unitPrice = Number(item.unit_price_dollar) || 0;

        // If unit price is 0/null but subtotal exists, calculate from subtotal/quantity
        if (unitPrice === 0 && subtotal > 0 && quantity > 0) {
          unitPrice = subtotal / quantity;
        }

        return {
          tempId: `temp_${Date.now()}_${index}`,
          itemized_estimate_id: `temp_${index}`,
          code: item.code || '',
          description: item.description || '',
          unit_price_dollar: unitPrice,
          quantity: quantity,
          sub_total_dollar: subtotal,
          cost_type: (item.cost_type || 'all') as 'all' | 'labor' | 'material',
          duration_hour: Number(item.duration_hour) || 0,
          estimate_id: 'temp_estimate',
          is_optional: Boolean(item.is_optional),
        };
      });

      setLineItems(initialItems);
    } else {
      setErrorMessage('No estimate data found. Please return to the Smart Assistant.');
    }
  }, [location.state]);

  // Recalculate totals whenever line items, tax rate, or discount change
  useEffect(() => {
    calculateTotals();
  }, [lineItems, taxRate, discount]);

  const calculateTotals = () => {
    let subtotal = 0;
    let totalHours = 0;
    let totalLaborHours = 0;
    let optionalDollar = 0;
    let requiredDollar = 0;

    lineItems.forEach((item: EditableLineItem) => {
      subtotal += item.sub_total_dollar || 0;
      totalHours += item.duration_hour || 0;

      // Calculate labor hours from labor items
      if (item.cost_type === 'labor') {
        totalLaborHours += item.duration_hour || 0;
      }

      if (item.is_optional) {
        optionalDollar += item.sub_total_dollar || 0;
      } else {
        requiredDollar += item.sub_total_dollar || 0;
      }
    });

    const taxAmount = subtotal * (taxRate / 100);
    const finalTotal = subtotal + taxAmount - discount;

    // Calculate days and hours from total labor hours
    const days = Math.floor(totalLaborHours / 8);
    const hours = totalLaborHours % 8;

    setTimeToCompleteDays(days);
    setTimeToCompleteHours(hours);

    setTotals({
      subtotal,
      taxAmount,
      finalTotal: finalTotal > 0 ? finalTotal : 0,
      totalHours,
      totalLaborHours,
      optionalDollar,
      requiredDollar,
    });
  };

  const handleLineItemChange = (tempId: string, field: keyof EditableLineItem, value: string | number) => {
    setLineItems(prevItems =>
      prevItems.map(item => {
        if (item.tempId === tempId) {
          const updatedItem = { ...item };

          // Recalculate subtotal if unit price or quantity changed
          if (field === 'unit_price_dollar' || field === 'quantity') {
            const unitPrice = field === 'unit_price_dollar' ? (parseFloat(value as string) || 0) : item.unit_price_dollar;
            const quantity = field === 'quantity' ? (parseFloat(value as string) || 0) : item.quantity;
            updatedItem.sub_total_dollar = unitPrice * quantity;
            updatedItem[field] = parseFloat(value as string) || 0;

            // For labor items, update duration_hour to match quantity
            if (field === 'quantity' && item.cost_type === 'labor') {
              updatedItem.duration_hour = quantity;
            }
          } else if (field === 'description') {
            updatedItem.description = value as string;
          }

          return updatedItem;
        }
        return item;
      })
    );
  };

  const handleDeleteLineItem = (tempId: string) => {
    if (lineItems.length <= 1) {
      alert('Cannot delete the last line item. At least one item is required.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this line item?')) {
      setLineItems(prevItems => prevItems.filter(item => item.tempId !== tempId));
    }
  };

  const handleBackToAssistant = () => {
    navigate('/main/project/estimate/smartassistant', {
      state: {
        selectedProjectId: selectedCustomer?.project_id,
        selectedCustomer: selectedCustomer,
      }
    });
  };

  const handleSaveEstimate = async () => {
    // Validation
    if (!estimateName.trim()) {
      setErrorMessage('Please enter an estimate name.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (lineItems.length === 0) {
      setErrorMessage('At least one line item is required.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!selectedCustomer) {
      setErrorMessage('Customer information is missing.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      // Create combined payload with estimate and itemized estimates
      const payload: EstimateWithItemsRequest = {
        name: estimateName,
        description: estimateDescription,
        terms_and_conditions: termsAndConditions as any,
        project_id: parseInt(selectedCustomer.project_id),
        customer_id: parseInt(selectedCustomer.customer_id),
        time_to_complete_hr: timeToCompleteHours,
        time_to_complete_day: timeToCompleteDays,
        tax_rate: taxRate,
        discount_usd: discount,
        itemized_estimates: lineItems.map((item) => ({
          code: item.code,
          description: item.description,
          unit_price_dollar: item.unit_price_dollar.toString(),
          sub_total_dollar: item.sub_total_dollar.toString(),
          cost_type: item.cost_type,
          duration_hour: item.duration_hour.toString(),
          quantity: item.quantity,
          is_optional: item.is_optional,
        })),
      };

      // Single API call to create estimate with items
      const estimateResponse = await EstimateApi.createEstimate(payload);
      const estimate_id = estimateResponse.data.attributes.estimate_id;

      setSavedEstimateId(estimate_id);
      setIsSaved(true);
      setSuccessMessage('Estimate created successfully!');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };




  const handleEdit = () => {
    if (!savedEstimateId || !selectedCustomer) {
      setErrorMessage("Error: Estimate not found");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Calculate optional and non-optional hours
    let nonOptionalHour = 0;
    let optionalHour = 0;

    lineItems.forEach((item) => {
      if (item.is_optional) {
        optionalHour += item.duration_hour || 0;
      } else {
        nonOptionalHour += item.duration_hour || 0;
      }
    });

    // Construct the complete EstimateProjectCustomer object
    const savedEstimate: EstimateProjectCustomer = {
      estimate_id: savedEstimateId,
      name: estimateName,
      description: estimateDescription,
      time_to_complete_hr: timeToCompleteHours,
      time_to_complete_day: timeToCompleteDays,
      terms_and_conditions: termsAndConditions,
      tax_rate: taxRate,
      discount_usd: discount,
      total_dollar: totals.subtotal,
      tax_amount: totals.taxAmount,
      final_total: totals.finalTotal,
      total_hour: totals.totalHours,
      non_optional_dollar: totals.requiredDollar,
      non_optional_hour: nonOptionalHour,
      optional_dollar: totals.optionalDollar,
      optional_hour: optionalHour,
      project_id: selectedCustomer.project_id,
      customer_id: selectedCustomer.customer_id,
      project_name: selectedCustomer.name,
      project_description: selectedCustomer.project_description || '',
      project_type: selectedCustomer.project_type || '',
      //project_start_date: selectedCustomer.project_start_date || '',
      //project_end_date: selectedCustomer.project_end_date || '',
      customer_first_name: selectedCustomer.customer_first_name,
      customer_last_name: selectedCustomer.customer_last_name,
      customer_address_1: selectedCustomer.customer_address_1,
      customer_address_2: selectedCustomer.customer_address_2,
      customer_city: selectedCustomer.customer_city,
      customer_state: selectedCustomer.customer_state,
      customer_zip: selectedCustomer.customer_zip,
      customer_phone: selectedCustomer.customer_phone,
      customer_email: selectedCustomer.customer_email,
    };

    navigate('/main/project/estimate/update', { state: { estimate: savedEstimate } });
  };



  const handlePDFView = async () => {
    setErrorMessage('');

    if (savedEstimateId) {
      try {
        const pdfBlob = await EstimateApi.viewEstimatePdf(savedEstimateId);
        const url = URL.createObjectURL(pdfBlob);
        window.open(url, '_blank');
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      }
    }
    else {
      setErrorMessage("Error: estimate not found");
    }
  };

  const handleSendToCustomer = () => {
    if (!savedEstimateId || !selectedCustomer) {
      setErrorMessage("Error: Estimate not found");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Calculate optional and non-optional hours
    let nonOptionalHour = 0;
    let optionalHour = 0;

    lineItems.forEach((item) => {
      if (item.is_optional) {
        optionalHour += item.duration_hour || 0;
      } else {
        nonOptionalHour += item.duration_hour || 0;
      }
    });

    // Construct the complete EstimateProjectCustomer object
    const savedEstimate: EstimateProjectCustomer = {
      estimate_id: savedEstimateId,
      name: estimateName,
      description: estimateDescription,
      time_to_complete_hr: timeToCompleteHours,
      time_to_complete_day: timeToCompleteDays,
      terms_and_conditions: termsAndConditions,
      tax_rate: taxRate,
      discount_usd: discount,
      total_dollar: totals.subtotal,
      tax_amount: totals.taxAmount,
      final_total: totals.finalTotal,
      total_hour: totals.totalHours,
      non_optional_dollar: totals.requiredDollar,
      non_optional_hour: nonOptionalHour,
      optional_dollar: totals.optionalDollar,
      optional_hour: optionalHour,
      project_id: selectedCustomer.project_id,
      customer_id: selectedCustomer.customer_id,
      project_name: selectedCustomer.name,
      project_description: selectedCustomer.project_description || '',
      project_type: selectedCustomer.project_type || '',
      //project_start_date: selectedCustomer.project_start_date || '',
      //project_end_date: selectedCustomer.project_end_date || '',
      customer_first_name: selectedCustomer.customer_first_name,
      customer_last_name: selectedCustomer.customer_last_name,
      customer_address_1: selectedCustomer.customer_address_1,
      customer_address_2: selectedCustomer.customer_address_2,
      customer_city: selectedCustomer.customer_city,
      customer_state: selectedCustomer.customer_state,
      customer_zip: selectedCustomer.customer_zip,
      customer_phone: selectedCustomer.customer_phone,
      customer_email: selectedCustomer.customer_email,
    };

    navigate('/main/project/estimate/tocustomer', { state: { estimate: savedEstimate } });
  };

  if (errorMessage) {
    return (
      <>
        <HorizontalMenu />
        <div className="container mx-auto max-w-7xl px-4 py-6">
          <Breadcrumbs items={breadcrumbs} />
          <Alert variant="error" className="mt-6">
            {errorMessage}
          </Alert>
          <Button onClick={() => navigate('/main/project/estimate/smartassistant')} className="mt-4">
            <ArrowLeft size={20} />
            Back to Smart Assistant
          </Button>
        </div>
      </>
    );
  }

  if (!selectedCustomer || lineItems.length === 0) {
    return (
      <>
        <HorizontalMenu />
        <div className="container mx-auto max-w-7xl px-4 py-6">
          <Breadcrumbs items={breadcrumbs} />
          <div className="flex justify-center items-center py-12">
            <p>Loading estimate...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <HorizontalMenu />
      <div className="container mx-auto max-w-7xl px-4 py-6">
        <Breadcrumbs items={breadcrumbs} />

        {/* Success Message */}
        {successMessage && (
          <Alert variant="success" className="mt-6">
            {successMessage}
          </Alert>
        )}

        {/* Error Message */}
        {errorMessage && (
          <Alert variant="error" className="mt-6">
            {errorMessage}
          </Alert>
        )}

        <div className="mt-6">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
              {isSaved ? 'Estimate Saved Successfully' : 'AI Generated Estimate - Final Review'}
            </h1>
            <p className="text-xs md:text-sm text-paragraph">
              {isSaved
                ? 'Your estimate has been saved. You can now view the PDF, send it to the customer, or edit it further.'
                : 'Review and edit your AI-generated estimate. Modify prices, quantities, tax rate, and discount as needed.'
              }
            </p>
          </div>

          {/* Action Buttons */}
          {!isSaved ? (
            <div className="mb-6 flex flex-col sm:flex-row gap-3">
              <Button variant="inverted" size="medium" onClick={handleBackToAssistant} className="w-full sm:w-auto">
                <ArrowLeft size={18} />
                Back to Assistant
              </Button>
              <Button size="medium" onClick={handleSaveEstimate} disabled={isSubmitting} className="w-full sm:w-auto">
                <Save size={18} />
                {isSubmitting ? 'Saving...' : 'Save Estimate'}
              </Button>
            </div>
          ) : (
            <div className="mb-6 flex flex-col sm:flex-row gap-3">
              <Button variant="inverted" size="medium" onClick={handleEdit} className="w-full sm:w-auto">
                <Edit size={18} />
                Edit Estimate
              </Button>
              <Button size="medium" onClick={handlePDFView} className="w-full sm:w-auto">
                <FileText size={18} />
                PDF View
              </Button>
              <Button size="medium" onClick={handleSendToCustomer} className="w-full sm:w-auto">
                <Send size={18} />
                Send to Customer
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content - 2 columns */}
            <div className="lg:col-span-2 space-y-6">
              {/* Project and Customer Info - Compact */}
              <div className="p-3 md:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs md:text-sm">
                  <div>
                    <strong className="font-semibold text-foreground">Project:</strong>
                    <span className="ml-1 text-gray-700">{selectedCustomer.name}</span>
                  </div>
                  <div>
                    <strong className="font-semibold text-foreground">Customer:</strong>
                    <span className="ml-1 text-gray-700">
                      {selectedCustomer.customer_first_name} {selectedCustomer.customer_last_name}
                    </span>
                  </div>
                </div>
              </div>

              {/* Estimate Details */}
              <div className="p-4 bg-white border border-gray-200 rounded-lg space-y-4">
                <h2 className="text-base md:text-lg font-semibold text-foreground">Estimate Details</h2>

                {!isSaved ? (
                  <>
                    <div>
                      <Label htmlFor="estimateName">
                        Estimate Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="estimateName"
                        value={estimateName}
                        onChange={(e) => setEstimateName(e.target.value)}
                        placeholder="Enter estimate name"
                      />
                    </div>

                    <div>
                      <Label htmlFor="estimateDescription">Estimate Description</Label>
                      <Textarea
                        id="estimateDescription"
                        value={estimateDescription}
                        onChange={(e) => setEstimateDescription(e.target.value)}
                        placeholder="Enter description"
                        rows={3}
                        className="min-h-[80px]"
                      />
                    </div>

                    {/* Tax Rate and Discount */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="taxRate">Tax Rate (%)</Label>
                        <Input
                          id="taxRate"
                          type="number"
                          min={0}
                          max={99}
                          step="0.1"
                          value={taxRate === 0 ? '' : taxRate.toString()}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            if (isNaN(value) || value < 0) {
                              setTaxRate(0);
                            } else {
                              setTaxRate(Math.min(value, 99));
                            }
                          }}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label htmlFor="discount">Discount ($)</Label>
                        <Input
                          id="discount"
                          type="number"
                          min={0}
                          step="10"
                          value={discount === 0 ? '' : discount.toString()}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            if (isNaN(value) || value < 0) {
                              setDiscount(0);
                            } else {
                              setDiscount(value);
                            }
                          }}
                          placeholder="0"
                        />
                      </div>
                    </div>

                    {/* Estimated Time to Complete */}
                    <div>
                      <Label>Estimated Time to Complete</Label>
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min={0}
                            max={365}
                            step="1"
                            placeholder="0"
                            className="w-20"
                            value={timeToCompleteDays === 0 ? '' : timeToCompleteDays.toString()}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              if (isNaN(value) || value < 0) {
                                setTimeToCompleteDays(0);
                              } else {
                                setTimeToCompleteDays(Math.min(Math.floor(value), 365));
                              }
                            }}
                          />
                          <span className="text-sm text-gray-700">Days</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min={0}
                            max={23}
                            step="1"
                            placeholder="0"
                            className="w-20"
                            value={timeToCompleteHours === 0 ? '' : timeToCompleteHours.toString()}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              if (isNaN(value) || value < 0) {
                                setTimeToCompleteHours(0);
                              } else {
                                setTimeToCompleteHours(Math.min(Math.floor(value), 23));
                              }
                            }}
                          />
                          <span className="text-sm text-gray-700">Hours</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        Calculated from labor hours: {Math.floor(totals.totalLaborHours)} hrs
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs md:text-sm">
                      <div>
                        <span className="font-medium text-gray-600">Name:</span>
                        <span className="ml-2 font-semibold">{estimateName}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Tax:</span>
                        <span className="ml-2">{taxRate}%</span>
                        <span className="mx-2 text-gray-400">|</span>
                        <span className="font-medium text-gray-600">Discount:</span>
                        <span className="ml-2">${formatCurrency(discount.toFixed(2))}</span>
                      </div>
                    </div>

                    {estimateDescription && (
                      <div className="text-xs md:text-sm">
                        <span className="font-medium text-gray-600">Description:</span>
                        <span className="ml-2 text-gray-700">{estimateDescription}</span>
                      </div>
                    )}

                    <div className="text-xs md:text-sm">
                      <span className="font-medium text-gray-600">Time to Complete:</span>
                      <span className="ml-2">
                        {timeToCompleteDays > 0 && `${timeToCompleteDays} day${timeToCompleteDays !== 1 ? 's' : ''}`}
                        {timeToCompleteDays > 0 && timeToCompleteHours > 0 && ', '}
                        {timeToCompleteHours > 0 && `${timeToCompleteHours} hour${timeToCompleteHours !== 1 ? 's' : ''}`}
                        {timeToCompleteDays === 0 && timeToCompleteHours === 0 && 'Not specified'}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Itemized Line Items */}
              <div className="p-4 bg-white border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base md:text-lg font-semibold text-foreground">Line Items ({lineItems.length})</h2>
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex items-center gap-2 text-primary hover:text-primary-hover text-sm"
                  >
                    {showDetails ? (
                      <>
                        <ChevronUp size={20} /> Hide Details
                      </>
                    ) : (
                      <>
                        <ChevronDown size={20} /> Show Details
                      </>
                    )}
                  </button>
                </div>

                {showDetails && (
                  <>
                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-100 border-b-2 border-gray-300">
                            <th className="text-left p-2 text-sm font-semibold">Code</th>
                            <th className="text-left p-2 text-sm font-semibold">Description</th>
                            <th className="text-right p-2 text-sm font-semibold">Unit Price</th>
                            <th className="text-right p-2 text-sm font-semibold">Quantity</th>
                            <th className="text-center p-2 text-sm font-semibold">Type</th>
                            <th className="text-right p-2 text-sm font-semibold">Hours</th>
                            <th className="text-right p-2 text-sm font-semibold">Subtotal</th>
                            <th className="text-center p-2 text-sm font-semibold">Optional</th>
                            {!isSaved && <th className="text-center p-2 text-sm font-semibold">Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {lineItems.map((item) => (
                            <tr key={item.tempId} className="border-b border-gray-200 hover:bg-gray-50">
                              <td className="p-2 text-sm">{item.code}</td>
                              <td className="p-2 text-sm">
                                {!isSaved ? (
                                  <input
                                    type="text"
                                    value={item.description}
                                    onChange={(e) => handleLineItemChange(item.tempId, 'description', e.target.value)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    title={item.description}
                                  />
                                ) : (
                                  <span title={item.description}>{item.description}</span>
                                )}
                              </td>
                              <td className="p-2 text-right">
                                {!isSaved ? (
                                  <input
                                    type="number"
                                    min={0}
                                    step="0.1"
                                    value={item.unit_price_dollar === 0 ? '' : item.unit_price_dollar.toString()}
                                    onChange={(e) => handleLineItemChange(item.tempId, 'unit_price_dollar', e.target.value)}
                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                                    placeholder="0"
                                  />
                                ) : (
                                  <span className="text-sm">${formatCurrency(item.unit_price_dollar.toFixed(2))}</span>
                                )}
                              </td>
                              <td className="p-2 text-right">
                                {!isSaved ? (
                                  <input
                                    type="number"
                                    min={0}
                                    step="1"
                                    value={item.quantity === 0 ? '' : item.quantity.toString()}
                                    onChange={(e) => handleLineItemChange(item.tempId, 'quantity', e.target.value)}
                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                                    placeholder="0"
                                  />
                                ) : (
                                  <span className="text-sm">{item.quantity}</span>
                                )}
                              </td>
                              <td className="p-2 text-center">
                                <span className={`px-2 py-1 rounded text-xs ${item.cost_type === 'labor'
                                    ? 'bg-blue-100 text-blue-800'
                                    : item.cost_type === 'material'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                  {item.cost_type}
                                </span>
                              </td>
                              <td className="p-2 text-sm text-right">
                                {item.duration_hour && item.duration_hour > 0 ? item.duration_hour : '-'}
                              </td>
                              <td className="p-2 text-sm text-right font-semibold">
                                ${formatCurrency((item.sub_total_dollar || 0).toFixed(2))}
                              </td>
                              <td className="p-2 text-center">
                                {item.is_optional ? (
                                  <span className="text-yellow-600">✓</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              {!isSaved && (
                                <td className="p-2 text-center">
                                  <button
                                    onClick={() => handleDeleteLineItem(item.tempId)}
                                    className="text-red-600 hover:text-red-800 p-1"
                                    title="Delete line item"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-4">
                      {lineItems.map((item) => (
                        <div key={item.tempId} className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <div className="space-y-3">
                            {/* Code and Type */}
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-semibold text-gray-700">Code: {item.code}</span>
                              <span className={`px-2 py-1 rounded text-xs ${item.cost_type === 'labor'
                                  ? 'bg-blue-100 text-blue-800'
                                  : item.cost_type === 'material'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                {item.cost_type}
                              </span>
                            </div>

                            {/* Description */}
                            <div>
                              <label className="text-xs text-gray-600 block mb-1">Description</label>
                              {!isSaved ? (
                                <input
                                  type="text"
                                  value={item.description}
                                  onChange={(e) => handleLineItemChange(item.tempId, 'description', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                />
                              ) : (
                                <p className="text-sm text-foreground">{item.description}</p>
                              )}
                            </div>

                            {/* Unit Price and Quantity */}
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-gray-600 block mb-1">Unit Price</label>
                                {!isSaved ? (
                                  <input
                                    type="number"
                                    min={0}
                                    step="0.1"
                                    value={item.unit_price_dollar === 0 ? '' : item.unit_price_dollar.toString()}
                                    onChange={(e) => handleLineItemChange(item.tempId, 'unit_price_dollar', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                    placeholder="0"
                                  />
                                ) : (
                                  <p className="text-sm font-semibold">${formatCurrency(item.unit_price_dollar.toFixed(2))}</p>
                                )}
                              </div>
                              <div>
                                <label className="text-xs text-gray-600 block mb-1">Quantity</label>
                                {!isSaved ? (
                                  <input
                                    type="number"
                                    min={0}
                                    step="1"
                                    value={item.quantity === 0 ? '' : item.quantity.toString()}
                                    onChange={(e) => handleLineItemChange(item.tempId, 'quantity', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                    placeholder="0"
                                  />
                                ) : (
                                  <p className="text-sm font-semibold">{item.quantity}</p>
                                )}
                              </div>
                            </div>

                            {/* Hours and Subtotal */}
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-gray-600 block mb-1">Hours</label>
                                <p className="text-sm font-semibold">
                                  {item.duration_hour && item.duration_hour > 0 ? item.duration_hour : '-'}
                                </p>
                              </div>
                              <div>
                                <label className="text-xs text-gray-600 block mb-1">Subtotal</label>
                                <p className="text-sm font-semibold text-primary">
                                  ${formatCurrency((item.sub_total_dollar || 0).toFixed(2))}
                                </p>
                              </div>
                            </div>

                            {/* Optional and Delete */}
                            <div className="flex items-center justify-between pt-2 border-t border-gray-300">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-600">Optional:</span>
                                {item.is_optional ? (
                                  <span className="text-yellow-600 font-semibold">✓ Yes</span>
                                ) : (
                                  <span className="text-gray-400">No</span>
                                )}
                              </div>
                              {!isSaved && (
                                <button
                                  onClick={() => handleDeleteLineItem(item.tempId)}
                                  className="text-red-600 hover:text-red-800 flex items-center gap-1 text-sm"
                                >
                                  <Trash2 size={16} />
                                  Delete
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {!showDetails && (
                  <div className="text-center text-gray-500 py-3 text-xs md:text-sm">
                    {lineItems.length} line item{lineItems.length !== 1 ? 's' : ''} • Total: ${formatCurrency(totals.subtotal.toFixed(2))}
                  </div>
                )}
              </div>
            </div>

            {/* Financial Summary Sidebar - 1 column */}
            <div className="lg:col-span-1">
              <div className="sticky top-6 max-h-[calc(100vh-4rem)] overflow-y-auto space-y-6">
                {/* Financial Summary */}
                <div className="p-4 bg-gradient-to-br from-primary/5 to-primary/10 border-2 border-primary/30 rounded-lg shadow-sm">
                  <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Financial Summary</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-xs md:text-sm">
                      <span className="text-gray-700">Total (before tax):</span>
                      <span className="font-semibold">${formatCurrency(totals.subtotal.toFixed(2))}</span>
                    </div>

                    {taxRate > 0 && (
                      <div className="flex justify-between text-xs md:text-sm">
                        <span className="text-gray-700">Tax ({taxRate}%):</span>
                        <span className="font-semibold">${formatCurrency(totals.taxAmount.toFixed(2))}</span>
                      </div>
                    )}

                    {discount > 0 && (
                      <div className="flex justify-between text-xs md:text-sm">
                        <span className="text-gray-700">Discount:</span>
                        <span className="font-semibold text-red-600">-${formatCurrency(discount.toFixed(2))}</span>
                      </div>
                    )}

                    <div className="pt-3 border-t-2 border-primary/30">
                      <div className="flex justify-between items-center">
                        <span className="text-base md:text-lg font-bold text-foreground">Final Total:</span>
                        <span className="text-xl md:text-2xl font-bold text-primary">
                          ${formatCurrency(totals.finalTotal.toFixed(2))}
                        </span>
                      </div>
                    </div>

                    {totals.optionalDollar > 0 && (
                      <div className="mt-4 pt-3 border-t border-gray-300 text-xs text-gray-600">
                        <div className="flex justify-between">
                          <span>Optional items:</span>
                          <span>${formatCurrency(totals.optionalDollar.toFixed(2))}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Required items:</span>
                          <span>${formatCurrency(totals.requiredDollar.toFixed(2))}</span>
                        </div>
                      </div>
                    )}

                    {totals.totalLaborHours > 0 && (
                      <div className="mt-4 pt-3 border-t border-gray-300 text-xs text-gray-600">
                        <div className="flex justify-between">
                          <span>Total Labor Hours:</span>
                          <span className="font-semibold">{totals.totalLaborHours.toFixed(1)} hrs</span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>≈ {timeToCompleteDays} days, {timeToCompleteHours} hours</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Info Alert */}
                  {!isSaved && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-gray-700">
                      <div className="flex items-start gap-2">
                        <AlertCircle size={14} className="text-blue-600 mt-0.5 shrink-0" />
                        <div>
                          <strong>Note:</strong> All totals update automatically as you edit line items, tax rate, or discount.
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Terms and Conditions */}
                <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">Terms and Conditions</h3>
                  {!isSaved && <Label className="mb-2 text-xs md:text-sm">Update it and save it with the estimate! </Label>}

                  {!isSaved ? (
                    <Textarea
                      value={termsAndConditions}
                      onChange={(e) => setTermsAndConditions(e.target.value)}
                      rows={8}
                      className="min-h-[180px] text-xs"
                      placeholder="Enter terms and conditions"
                    />
                  ) : (
                    <div className="text-xs text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded border border-gray-200 max-h-[400px] overflow-y-auto">
                      {termsAndConditions || 'No terms and conditions specified'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SmartGeneratedEstimatePage;